import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { IsStringEmpty, IsVarEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import type { DeviceModalCapabilityFlags } from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID,
    DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY,
    DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP,
    DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS,
    DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS,
    DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID,
    DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING,
    DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH,
    DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID,
    DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS,
    DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD,
    DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS
} from '../Spec/DeviceModalCapabilityKeys'
import {
    DeviceAdminRequest,
    DeviceConfigurationForm,
    DeviceDetailDTO,
    DeviceFormPresentationContext
} from '../Types/Types'
import { DEFAULT_DEVICE_TYPE } from '../Constants/Constants'
import { resolveDeviceModalCapabilityFlags } from './resolveDeviceModalCapabilityFlags'

type DeviceRequestCapabilityExtras = Readonly<{
    dnisNumbers?: string[]
    isAddMode?: boolean
    updateExistingItems?: boolean
}>

/** Optional context when mapping a device DTO into the modal form (for example list partition label). */
export type MapDeviceDtoToFormContext = {
    partitionDisplayLabel?: string
    /** Admin list scope: partition {@code IDInTelSys} used for add-mode {@code partIDInTelSys} defaults. */
    partitionIdInTelSys?: number
}

/**
 * Trims an optional form string and converts blank values to {@code undefined}.
 *
 * @param value - Raw form string.
 * @returns Trimmed string when present, otherwise {@code undefined}.
 */
const trimToUndefined = (value?: string): Definable<string> => {
    const trimmedValue: string = value?.trim() ?? EMPTY_STRING

    return IsStringEmpty(trimmedValue) ? undefined : trimmedValue
}

/**
 * Parses an optional integer form field while normalising blank and invalid values.
 *
 * @param value - Raw numeric form string.
 * @returns Parsed integer, or {@code null} when the field is blank or invalid.
 */
const parseOptionalNumber = (value: string): Nullable<number> => {
    if (IsStringEmpty(value)) {
        return null
    }

    const parsedValue: number = Number.parseInt(value, 10)

    return Number.isNaN(parsedValue) ? null : parsedValue
}

/**
 * Maps a device detail DTO to the modal form shape.
 *
 * @param dto - Device API payload, or null for add-mode defaults.
 * @param context - Optional UI context such as the partition label from the admin list scope.
 * @returns Form values matching {@link DeviceConfigurationForm}.
 */
export const mapDeviceDtoToForm = (
    dto: Nullable<DeviceDetailDTO>,
    context?: MapDeviceDtoToFormContext
): DeviceConfigurationForm => {
    if (IsVarEmpty(dto)) {
        const partIdFromListScope: string =
            isVarNotEmpty(context?.partitionIdInTelSys)
                ? String(context.partitionIdInTelSys)
                : EMPTY_STRING

        return {
            general: {
                deviceGuid: EMPTY_STRING,
                deviceNumber: EMPTY_STRING,
                description: EMPTY_STRING,
                deviceType: DEFAULT_DEVICE_TYPE,
                partitionDisplay: context?.partitionDisplayLabel ?? EMPTY_STRING,
                partIDInTelSys: partIdFromListScope,
                idOnTelSys: EMPTY_STRING,
                idOnTelSysOwner: EMPTY_STRING,
                idOnTelSysDivision: EMPTY_STRING,
                deviceNumberInternal: EMPTY_STRING,
                telSysUserID: EMPTY_STRING,
                telSysPassword: EMPTY_STRING,
                primaryEmail: EMPTY_STRING,
                overrideMonitoring: false,
                disableRTMonitoring: false,
                updateExistingItems: false
            }
        }
    }

    const partitionLabelFromDto: Definable<string> = dto.PartitionDescription
    const partitionLabelFromContext: Definable<string> = context?.partitionDisplayLabel
    let partitionDisplay: string = EMPTY_STRING

    if (isStringNotEmpty(partitionLabelFromDto)) {
        partitionDisplay = partitionLabelFromDto
    } else if (isStringNotEmpty(partitionLabelFromContext)) {
        partitionDisplay = partitionLabelFromContext
    } else if (!IsVarEmpty(dto.PartIDInTelSys)) {
        partitionDisplay = String(dto.PartIDInTelSys)
    }

    return {
        general: {
            deviceGuid: dto.DeviceGUID ?? EMPTY_STRING,
            deviceNumber: dto.DeviceNumber ?? EMPTY_STRING,
            description: dto.Description ?? EMPTY_STRING,
            deviceType: trimToUndefined(dto.DeviceType) ?? DEFAULT_DEVICE_TYPE,
            partitionDisplay,
            partIDInTelSys: IsVarEmpty(dto.PartIDInTelSys) ? EMPTY_STRING : String(dto.PartIDInTelSys),
            idOnTelSys: dto.IDOnTelSys ?? EMPTY_STRING,
            idOnTelSysOwner: dto.IDOnTelSysOwner ?? EMPTY_STRING,
            idOnTelSysDivision: IsVarEmpty(dto.IDOnTelSysDivision) ? EMPTY_STRING : String(dto.IDOnTelSysDivision),
            deviceNumberInternal: IsVarEmpty(dto.DeviceNumberInternal)
                ? EMPTY_STRING
                : String(dto.DeviceNumberInternal),
            telSysUserID: dto.TelSysUserID ?? EMPTY_STRING,
            telSysPassword: dto.TelSysPassword ?? EMPTY_STRING,
            primaryEmail: dto.PrimaryEmail ?? EMPTY_STRING,
            overrideMonitoring: dto.OverrideMonitoring ?? false,
            disableRTMonitoring: dto.DisableRTMonitoring ?? false,
            updateExistingItems: false
        }
    }
}

const applyDeviceModalCapabilityGates = (
    request: DeviceAdminRequest,
    flags: DeviceModalCapabilityFlags,
    extras?: DeviceRequestCapabilityExtras
): void => {
    if (!flags[DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID]) {
        request.IDOnTelSys = undefined
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID]) {
        request.IDOnTelSysOwner = undefined
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_OWNING_DEPARTMENT_FULL_PATH]) {
        request.IDOnTelSysDivision = undefined
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS]) {
        request.DeviceNumberInternal = null
    }

    const showTelSysCredentialRow: boolean =
        flags[DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID] || flags[DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY]

    if (!showTelSysCredentialRow) {
        request.TelSysUserID = undefined
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD]) {
        request.TelSysPassword = undefined
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]) {
        request.PrimaryEmail = undefined
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_OVERRIDE_MONITORING]) {
        request.OverrideMonitoring = false
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_DISABLE_REALTIME_QUEUE_MONITORING]) {
        request.DisableRTMonitoring = false
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DEVICE_MEMBERSHIP]) {
        request.DeviceMembers = []
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_HUNT_GROUP_AGENT_MEMBERSHIP]) {
        request.ACDAgentMembers = []
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_HUNT_GROUP_DNIS_ASSOCIATIONS]) {
        request.DNISNumbers = undefined
    } else if (isVarNotEmpty(extras?.dnisNumbers)) {
        request.DNISNumbers = extras.dnisNumbers.length > 0 ? extras.dnisNumbers : undefined
    }

    if (!flags[DEVICE_MODAL_CAPABILITY_UPDATE_EXISTING_DUPLICATE_ITEMS] || extras?.isAddMode !== true) {
        request.UpdateExistingItems = undefined
    } else if (extras?.updateExistingItems === true) {
        request.UpdateExistingItems = true
    } else {
        request.UpdateExistingItems = undefined
    }
}

/**
 * Maps modal form values to the request payload for device create/update.
 *
 * @param form - Device configuration form state.
 * @param deviceMembers - Optional device members list for group devices (defaults to an empty list).
 * @param agentMembers - Optional ACD agent members list for group devices (defaults to an empty list).
 * @param presentation - When set, strips or neutralises fields that the Confluence capability matrix marks as not applicable.
 * @returns Request payload for device endpoints (including {@code PartIDInTelSys} when the form carries a partition id).
 */
export const mapDeviceFormToRequest = (
    form: DeviceConfigurationForm,
    deviceMembers?: string[],
    agentMembers?: string[],
    presentation?: DeviceFormPresentationContext
): DeviceAdminRequest => {
    const deviceType: string = trimToUndefined(form.general.deviceType) ?? DEFAULT_DEVICE_TYPE
    const memberDeviceNumbers: string[] = deviceMembers ?? []
    const memberAgentNumbers: string[] = agentMembers ?? []

    const resolvedPartIDInTelSys: Definable<number> = ((): Definable<number> => {
        if (IsStringEmpty(form.general.partIDInTelSys)) {
            return undefined
        }

        const parsed: number = Number.parseInt(form.general.partIDInTelSys, 10)

        return Number.isNaN(parsed) ? undefined : parsed
    })()

    const request: DeviceAdminRequest = {
        DeviceNumber: trimToUndefined(form.general.deviceNumber),
        Description: trimToUndefined(form.general.description),
        DeviceType: deviceType,
        PartIDInTelSys: resolvedPartIDInTelSys,
        IDOnTelSys: trimToUndefined(form.general.idOnTelSys),
        IDOnTelSysOwner: trimToUndefined(form.general.idOnTelSysOwner),
        IDOnTelSysDivision: trimToUndefined(form.general.idOnTelSysDivision),
        DeviceNumberInternal: parseOptionalNumber(form.general.deviceNumberInternal),
        TelSysUserID: trimToUndefined(form.general.telSysUserID),
        TelSysPassword: trimToUndefined(form.general.telSysPassword),
        PrimaryEmail: trimToUndefined(form.general.primaryEmail),
        OverrideMonitoring: form.general.overrideMonitoring,
        DisableRTMonitoring: form.general.disableRTMonitoring,
        DeviceMembers: memberDeviceNumbers,
        ACDAgentMembers: memberAgentNumbers
    }

    if (isVarNotEmpty(presentation)) {
        const flags: DeviceModalCapabilityFlags = resolveDeviceModalCapabilityFlags(deviceType, presentation.telSysType)

        applyDeviceModalCapabilityGates(request, flags, {
            dnisNumbers: presentation.dnisNumbers,
            isAddMode: presentation.isAddMode,
            updateExistingItems: form.general.updateExistingItems
        })
    }

    return request
}
